<script setup>
import { computed, ref } from 'vue'
import { useI18n } from '../../i18n'
import { formatStorageBytes, maintenanceDatasetLabel, totalStorageBytes, totalStorageRows } from './maintenanceUi'

const props = defineProps({
  overview: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const { locale, t } = useI18n()
const categories = computed(() => props.overview?.storage?.categories ?? [])
const rows = computed(() => totalStorageRows(categories.value))
const bytes = computed(() => totalStorageBytes(categories.value))
const expandedCategory = ref('')
const categoryNames = computed(() => ({
  TRANSACTIONS: t('maintenanceTransactions'),
  RECEIPTS: t('maintenanceReceipts'),
  OBSERVATIONS: t('maintenanceObservations'),
  REFERENCE: t('maintenanceReferenceData'),
}))

function number(value) {
  return new Intl.NumberFormat(locale.value).format(Number(value ?? 0))
}


function toggleCategory(category) {
  expandedCategory.value = expandedCategory.value === category ? '' : category
}

function visibleDatasets(category) {
  return (category.datasets ?? []).filter((dataset) => Number(dataset.activeRowCount) > 0 || Number(dataset.rowCount) > 0)
}

function hasInactiveCopies(category) {
  return visibleDatasets(category).some((dataset) => Number(dataset.activeRowCount) !== Number(dataset.rowCount))
}

function openSessions() {
  return (props.overview?.openSessionNumbers ?? []).join(', ')
}
</script>

<template>
  <section class="card maintenance-storage" aria-labelledby="maintenance-storage-title">
    <header class="section-heading maintenance-card-heading">
      <div>
        <p class="eyebrow">{{ t('maintenanceStorageEyebrow') }}</p>
        <h2 id="maintenance-storage-title">{{ t('maintenanceStorageTitle') }}</h2>
      </div>
      <span class="maintenance-approximate">{{ t('maintenanceApproximate') }}</span>
    </header>
    <p v-if="loading" class="muted">{{ t('loading') }}</p>
    <template v-else-if="overview">
      <div class="maintenance-storage-totals">
        <article><span>{{ t('maintenanceStorageSize') }}</span><strong>{{ formatStorageBytes(bytes) }}</strong></article>
        <article><span>{{ t('maintenanceStorageRows') }}</span><strong>{{ number(rows) }}</strong></article>
        <article><span>{{ t('maintenanceClosedShifts') }}</span><strong>{{ number(overview.eligibleShiftCount) }}</strong></article>
        <article><span>{{ t('maintenanceOpenShifts') }}</span><strong>{{ number(overview.openSessionNumbers?.length) }}</strong></article>
      </div>
      <div class="maintenance-storage-categories" role="list" :aria-label="t('maintenanceStorageCategories')">
        <article v-for="category in categories" :key="category.category" role="listitem"
          :class="{ expanded: expandedCategory === category.category }">
          <button type="button" :aria-expanded="expandedCategory === category.category"
            @click="toggleCategory(category.category)">
            <span>{{ categoryNames[category.category] ?? category.category }}</span>
            <strong>{{ number(category.activeRowCount ?? category.rowCount) }}</strong>
            <small>{{ formatStorageBytes(category.estimatedBytes) }}</small>
            <em>{{ expandedCategory === category.category ? t('maintenanceHideDetails') : t('maintenanceShowDetails') }}</em>
          </button>
          <div v-if="expandedCategory === category.category" class="maintenance-storage-breakdown"
            :class="{ 'has-physical': hasInactiveCopies(category) }">
            <p v-if="hasInactiveCopies(category)">{{ t('maintenanceCategoryExplanation') }}</p>
            <div v-if="hasInactiveCopies(category)" class="maintenance-storage-breakdown-heading">
              <span>{{ t('maintenanceDataset') }}</span>
              <span>{{ t('maintenanceActiveRows') }}</span>
              <span>{{ t('maintenanceStoredRows') }}</span>
            </div>
            <div v-for="dataset in visibleDatasets(category)" :key="dataset.tableKey" class="maintenance-storage-dataset">
              <span class="maintenance-storage-dataset-name">
                <span>{{ maintenanceDatasetLabel(dataset.tableKey, t) }}</span>
                <code>{{ dataset.tableKey }}</code>
              </span>
              <strong>{{ number(dataset.activeRowCount) }}</strong>
              <span v-if="hasInactiveCopies(category)">{{ number(dataset.rowCount) }}</span>
            </div>
            <p v-if="visibleDatasets(category).length === 0" class="maintenance-storage-empty">{{ t('maintenanceCategoryEmpty') }}</p>
            <p v-else-if="category.category === 'TRANSACTIONS' && openSessions()" class="maintenance-storage-context-note">
              {{ t('maintenanceCurrentShiftDataExplanation', { sessions: openSessions() }) }}
            </p>
            <p v-else-if="category.category === 'REFERENCE'" class="maintenance-storage-context-note">{{ t('maintenanceReferenceExplanation') }}</p>
          </div>
        </article>
      </div>
      <p class="maintenance-preserved-note">{{ t('maintenancePreservedData') }}</p>
    </template>
  </section>
</template>
